import {
  createStyleMixin,
  draggableMixin,
  createProxy,
  proxyInputMixin,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import {
  forwardAttrs,
  getComponentName,
  syncAttrs,
} from '@descope-ui/common/components-helpers';
import { compose } from '@descope-ui/common/utils';
import { ComboBoxClass } from '@descope-ui/descope-combo-box/class';
import { componentName as descopeInternalComponentName } from './descope-autocomplete-field-internal/AutocompleteFieldInternal';

export const componentName = getComponentName('autocomplete-field');

const customMixin = (superclass) =>
  class AutocompleteFieldMixinClass extends superclass {
    get defaultValue() {
      return this.getAttribute('default-value');
    }

    setDefaultValue() {
      if (this.defaultValue) {
        setTimeout(() => {
          this.inputElement.value = this.defaultValue;
        });
      }
    }

    init() {
      super.init?.();
      const template = document.createElement('template');

      template.innerHTML = `
      <${descopeInternalComponentName}
        tabindex="-1"
      ></${descopeInternalComponentName}>
      `;

      this.baseElement.appendChild(template.content.cloneNode(true));

      this.inputElement = this.shadowRoot.querySelector(
        descopeInternalComponentName,
      );

      forwardAttrs(this, this.inputElement, {
        includeAttrs: [
          'size',
          'bordered',
          'label',
          'required',
          'label-type',
          'placeholder',
          'full-width',
          'allow-custom-value',
          'min-search-length',
          'no-results-message',
          'error-fetching-results-message',
          'data-errormessage-value-missing',
          'invalid',
          'error-message',
          'readonly',
          'disabled',
          'st-host-direction',
          'st-error-message-icon',
          'st-error-message-icon-size',
          'st-error-message-icon-padding',
        ],
      });

      // This is required since when we remove the invalid attribute from the internal mappings field,
      // we want to reflect the change in the parent component
      syncAttrs(this, this.inputElement, { includeAttrs: ['invalid'] });
      this.inputElement.fetchResults = this.fetchResults;
      this.setDefaultValue();
    }

    set fetchResults(fetchResults) {
      this.inputElement.fetchResults = fetchResults;
    }
  };

const { host } = {
  host: { selector: () => ':host' },
};

export const AutocompleteFieldClass = compose(
  createStyleMixin({
    componentNameOverride: getComponentName('input-wrapper'),
  }),
  createStyleMixin({
    mappings: {
      hostWidth: { ...host, property: 'width' },
      hostDirection: { ...host, property: 'direction' },
      fontSize: { ...host },
      checkmarkDisplay: {
        selector: ComboBoxClass.componentName,
        property: ComboBoxClass.cssVarList.overlayCheckmarkDisplay,
      },
      itemPaddingInlineStart: {
        selector: ComboBoxClass.componentName,
        property: ComboBoxClass.cssVarList.overlayItemPaddingInlineStart,
      },
      itemPaddingInlineEnd: {
        selector: ComboBoxClass.componentName,
        property: ComboBoxClass.cssVarList.overlayItemPaddingInlineEnd,
      },
      selectedItemBackground: {
        selector: ComboBoxClass.componentName,
        property: ComboBoxClass.cssVarList.overlaySelectedItemBackground,
      },
      selectedItemHoverBackground: {
        selector: ComboBoxClass.componentName,
        property: ComboBoxClass.cssVarList.overlaySelectedItemHoverBackground,
      },
      selectedItemFocusBackground: {
        selector: ComboBoxClass.componentName,
        property: ComboBoxClass.cssVarList.overlaySelectedItemFocusBackground,
      },
      itemHoverBackground: {
        selector: ComboBoxClass.componentName,
        property: ComboBoxClass.cssVarList.overlayItemHoverBackground,
      },
    },
  }),
  draggableMixin,
  proxyInputMixin({
    proxyProps: ['value', 'selectionStart'],
    inputEvent: 'input',
    proxyParentValidation: true,
  }),
  componentNameValidationMixin,
  customMixin,
)(
  createProxy({
    slots: [],
    wrappedEleName: 'vaadin-custom-field',
    style: () => `
    :host {
      display: inline-flex;
      max-width: 100%;
      direction: ltr;
    }
    vaadin-custom-field {
      line-height: unset;
      width: 100%;
    }

    descope-autocomplete-field-internal {
      display: inline-block;
      box-sizing: border-box;
      user-select: none;
      width: 100%;
      max-width: 100%;
    }

    descope-autocomplete-field-internal ::slotted {
      padding: 0;
    }
  `,
    excludeAttrsSync: ['tabindex', 'error-message', 'label', 'style'],
    componentName,
  }),
);
